'use server';
import { revalidatePath } from 'next/cache';
import { cookies } from 'next/headers';
import { getLocale, getTranslations } from 'next-intl/server';
import { redirect } from 'next/navigation';
import { createClient } from './supabase/server';
import { invitationTokenFrom } from './invite-only';
import { api, ApiError, publicPost } from './api';
import { IMPERSONATION_COOKIE, IMPERSONATION_MAX_AGE_SECONDS } from './impersonation';
import { getRealSession, getSession, roleHome, isRole, safeNextPath, type Role } from './auth';
import { type NotificationFeed, getAgendaPrefs, adminCountPendingSupportRequests, type MySupportRequestDetail, type SupportLabelColor, type SupportRequestPriority, type SupportRequestStatus, getFeatureFlags } from './queries';
import { landingFor } from './pro-landing';
import { isPasswordValid } from './password-rules';

export type ActionState =
  | {
      error?: string;
      code?: string;
      checkoutUrl?: string;
      mfaRequired?: boolean;
      phoneSignupRequired?: boolean;
      phone?: string;
      challengeId?: string;
      next?: string;
    }
  | undefined;

const msg = (e: unknown) =>
  e instanceof ApiError
    ? e.status === 401
      ? 'Sessão expirada.'
      : 'Não foi possível concluir a ação.'
    : 'Erro inesperado.';

/** A rule the user can act on reaches us as a `code` FIELD in the body, never as prose (the API
 *  builds those in `apps/api/src/common/api-errors.ts`). This reads the field and compares it
 *  exactly against the vocabulary the caller knows how to say — an unknown code is not a
 *  half-translated sentence, it falls back to the generic message. */
const codeFrom = <C extends string>(e: unknown, known: ReadonlySet<string>): C | undefined =>
  e instanceof ApiError && e.code && known.has(e.code) ? (e.code as C) : undefined;

/** Council/registration refusals (`apps/api/src/professional/professional-errors.ts`),
 *  translated council-aware. */
const REGISTRATION_ERROR_CODES = new Set([
  'registration_invalid',
  'unknown_council',
  'verification_incomplete',
  'council_not_accepted',
  // The short public link (/pro/<slug>) the professional chose: already another pro's, or a
  // platform word. Both refusals are actionable, so they travel as codes like the rest.
  'slug_taken',
  'slug_reserved',
]);
async function registrationErrorMsg(e: unknown): Promise<string> {
  const code = codeFrom(e, REGISTRATION_ERROR_CODES);
  if (!code) return msg(e);
  const t = await getTranslations('councils');
  return t(`errors.${code}`);
}

/* ---------------- Auth (stays on Supabase) ---------------- */

export async function signIn(_prev: ActionState, formData: FormData): Promise<ActionState> {
  const t = await getTranslations('auth');
  const legacyKind = formData.get('identifier_kind') === 'phone' ? 'phone' : 'email';
  const identifier = String(formData.get('identifier') ?? formData.get(legacyKind) ?? '').trim();
  const password = String(formData.get('password') ?? '');
  const next = safeNextPath(String(formData.get('next') ?? '')); // block open-redirect via ?next=//evil.com
  const locale = await getLocale();
  let result: {
    session?: { access_token: string; refresh_token: string };
    challengeRequired?: boolean;
    challengeId?: string;
  };
  try {
    result = await publicPost('/auth/session/start', { identifier, password, locale });
  } catch {
    return { error: t('invalidCredentials') };
  }
  if (result.challengeRequired && result.challengeId) {
    return { mfaRequired: true, challengeId: result.challengeId, next: next || '/app' };
  }
  if (!result.session) return { error: t('invalidCredentials') };
  const { error } = await (await createClient()).auth.setSession(result.session);
  if (error) return { error: t('invalidCredentials') };
  const session = await getSession();
  redirect(next || (await landingFor(session?.role ?? 'patient')));
}

export async function signUp(_prev: ActionState, formData: FormData): Promise<ActionState> {
  const t = await getTranslations('auth');
  const legacyKind = formData.get('identifier_kind') === 'phone' ? 'phone' : 'email';
  const identifier = String(formData.get('identifier') ?? formData.get(legacyKind) ?? '').trim();
  const identifierKind = identifier.includes('@') ? 'email' : 'phone';
  const email = identifierKind === 'email' ? identifier : '';
  const password = String(formData.get('password') ?? '');
  const fullName = String(formData.get('full_name') ?? '').trim();

  // Browser-detected zone (hidden field). handle_new_user copies it to profiles/pro and
  // guards it: blank/empty AND invalid-non-empty values both fall back to the SP default
  // in the trigger (it probes the zone via 'at time zone'). Defense in depth, mappers.ts's
  // safeZone also falls back on read, so a bad zone can never throw. Never a business
  // timestamp, so no ambient-clock risk.
  const timezone = String(formData.get('timezone') ?? '').trim() || undefined;
  // The number every WhatsApp notification needs. handle_new_user copies it to profiles.phone
  // and drops anything with fewer than 8 digits, so a half-typed mask stores null rather than
  // an undialable contact. Left as typed: the API's normalizePhone owns E.164 at send time
  // (a leading + is the stated country), and a second normalizer here would fight it.
  const phone =
    (identifierKind === 'phone' ? identifier : String(formData.get('phone') ?? '').trim()) ||
    undefined;
  if (identifierKind === 'phone' && !(await getFeatureFlags()).phone_login) {
    return { error: t('phoneSignupUnavailable') };
  }
  if (password.length < 8) return { error: 'A senha precisa de ao menos 8 caracteres.' };
  // LGPD: consent is required and persisted (handle_new_user copies it to profiles).
  if (formData.get('terms') !== 'on')
    return { error: 'É preciso aceitar os Termos e a Política de Privacidade.' };

  const next = safeNextPath(String(formData.get('next') ?? ''));
  // Invite only: no account without an open clinic invitation behind it.
  const invitation = invitationTokenFrom(next);
  const invite = invitation
    ? await api
        .get<{ status: string; kind: string; role: string | null }>(`/public/invitations/${encodeURIComponent(invitation)}`)
        .catch(() => null)
    : null;
  if (invite?.status !== 'open') return { error: t('inviteOnly') };
  // The invitation decides the account: a studio owner or an invited professional gets a
  // professional account; everyone else starts as a patient account (staff become staff on
  // accepting). Never what the form says.
  const role: Role = invite.kind === 'owner' || (invite.kind === 'member' && invite.role === 'professional') ? 'professional' : 'patient';
  if (formData.get('password_confirm') !== null && formData.get('password_confirm') !== password)
    return { error: t('passwordMismatch') };
  // The API creates the account with the invitation's own e-mail, then she signs in and the
  // invitation is accepted in the same step (owner, 2026-11-03).
  if (formData.get('identifier') === null) {
    let account: { email: string };
    try {
      account = await publicPost<{ email: string }>(`/public/invitations/${encodeURIComponent(invitation!)}/account`, {
        fullName,
        password,
        phone: String(formData.get('phone') ?? '').trim() || undefined,
        timezone,
        locale: 'pt',
        termsAccepted: true,
      });
    } catch (error) {
      const code = error instanceof ApiError ? error.code : undefined;
      if (code === 'clinic_invitation_account_exists') return { error: t('emailAlreadyRegistered') };
      if (code === 'clinic_invitation_phone_required') return { error: t('phoneRequired') };
      if (code === 'clinic_invitation_closed') return { error: t('inviteOnly') };
      return { error: t('signupFailed') };
    }
    const supabase = await createClient();
    const { error: signInError } = await supabase.auth.signInWithPassword({ email: account.email, password });
    if (signInError) redirect(`/login?next=${encodeURIComponent(next!)}`);
    const accepted = await api
      .post<{ destination: string }>(`/invitations/${encodeURIComponent(invitation!)}/accept`)
      .catch(() => null);
    redirect(accepted?.destination ?? next!);
  }
  const refRaw = String(formData.get('ref') ?? '').trim();
  const ref = /^[A-Za-z0-9-]{4,20}$/.test(refRaw) ? refRaw.toUpperCase() : '';
  // Back to the invitation, which accepts it and moves on (setup for a new studio).
  const destination = next
    ? next
    : role === 'professional'
      ? `/onboarding/professional${ref ? `?ref=${ref}` : ''}`
      : (next ?? (role === 'patient' ? '/app/patient/forms' : roleHome(role)));
  // Chosen site language (cookie-based). handle_new_user persists it to profiles.locale so the
  // welcome e-mail fired right after signup goes out in the right language, not the 'pt' default.
  const locale = await getLocale();

  if (identifierKind === 'phone') {
    try {
      const result = await publicPost<{ challengeId: string }>('/auth/phone/signup/request', {
        phone,
        fullName,
        role,
        timezone,
        locale,
        ref: ref || undefined,
        termsAccepted: true,
      });
      return {
        phoneSignupRequired: true,
        phone,
        challengeId: result.challengeId,
        next: destination,
      };
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        return { error: t('phoneAlreadyRegistered') };
      }
      return { error: t('phoneSignupUnavailable') };
    }
  }

  const supabase = await createClient();
  const { error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: fullName,
        role,
        terms_accepted_at: new Date().toISOString(),
        timezone,
        locale,
        phone,
        // Optional; handle_new_user validates (mod-11) and drops anything invalid or taken.
        ...(role === 'patient' && String(formData.get('cpf') ?? '').trim()
          ? { cpf: String(formData.get('cpf')).replace(/\D/g, '').slice(0, 11) }
          : {}),
      },
    },
  });
  if (error) {
    if (error.message.toLowerCase().includes('phone signups are disabled')) {
      return { error: t('phoneSignupUnavailable') };
    }
    if (error.message.includes('already')) {
      return {
        error: t('emailAlreadyRegistered'),
      };
    }
    return { error: error.message };
  }
  // Fresh professionals get a best-effort welcome e-mail (Resend-deduped by user id).
  // signUp auto-sessions the account, so this authenticated call works right away (same
  // pattern as guestCheckout). A mail hiccup must never block signup, so it is swallowed.
  if (role === 'professional') {
    try {
      await api.post('/me/pro/welcome', {});
    } catch {
      // best-effort only
    }
  }
  // New patients land on the anamnesis (skippable) — unless a safe ?next= brought
  // them here mid-flow (e.g. booking), which must not be interrupted. Professionals
  // always get the (equally skippable) onboarding wizard; admins go straight home.
  redirect(destination);
}

export async function submitAuth(prev: ActionState, formData: FormData): Promise<ActionState> {
  return formData.get('mode') === 'criar' ? signUp(prev, formData) : signIn(prev, formData);
}

export async function beginPhoneLoginEnrollment(
  phone: string,
  password: string,
): Promise<{ challengeId?: string; error?: true }> {
  try {
    const locale = await getLocale();
    const result = await api.post<{ challengeId: string }>('/auth/phone/enroll/request', {
      phone,
      password,
      locale,
    });
    return { challengeId: result.challengeId };
  } catch {
    return { error: true };
  }
}

export async function activatePhoneLoginFactor(
  challengeId: string,
  code: string,
): Promise<{ ok?: true; error?: true }> {
  try {
    await api.post('/auth/phone/enroll/verify', { challengeId, code });
    revalidatePath('/app', 'layout');
    return { ok: true };
  } catch {
    return { error: true };
  }
}

export async function verifyPhoneSignup(
  challengeId: string,
  code: string,
  password: string,
): Promise<{ ok?: true; error?: true }> {
  try {
    const result = await publicPost<{
      session: { access_token: string; refresh_token: string };
    }>('/auth/phone/signup/verify', { challengeId, code, password });
    const { error } = await (await createClient()).auth.setSession(result.session);
    return error ? { error: true } : { ok: true };
  } catch {
    return { error: true };
  }
}

export async function verifyPhoneAuthSession(
  challengeId: string,
  code: string,
): Promise<{ ok?: true; error?: true }> {
  try {
    const result = await publicPost<{
      session: { access_token: string; refresh_token: string };
    }>('/auth/session/verify', { challengeId, code });
    const { error } = await (await createClient()).auth.setSession(result.session);
    return error ? { error: true } : { ok: true };
  } catch {
    return { error: true };
  }
}

export async function requestPhoneInvitation(
  phone: string,
): Promise<{ challengeId?: string; error?: true }> {
  try {
    const locale = await getLocale();
    const result = await publicPost<{ challengeId: string }>('/auth/phone/invitation/request', {
      phone,
      locale,
    });
    return { challengeId: result.challengeId };
  } catch {
    return { error: true };
  }
}

export async function verifyPhoneInvitation(
  challengeId: string,
  code: string,
  password: string,
): Promise<{ ok?: true; error?: true }> {
  try {
    const result = await publicPost<{
      session: { access_token: string; refresh_token: string };
    }>('/auth/phone/invitation/verify', { challengeId, code, password });
    const { error } = await (await createClient()).auth.setSession(result.session);
    return error ? { error: true } : { ok: true };
  } catch {
    return { error: true };
  }
}

export async function signOut(): Promise<void> {
  const supabase = await createClient();
  await supabase.auth.signOut();
  // Drop any impersonation with the session, or the next admin to sign in on
  // this browser would silently land inside the previous target's account.
  (await cookies()).delete(IMPERSONATION_COOKIE);
  redirect('/');
}

/* ---------------- Admin impersonation ("entrar como") ---------------- */

/** Cookie flags shared by set/clear. Not httpOnly on purpose — see lib/impersonation.ts. */
const impersonationCookie = {
  path: '/',
  sameSite: 'lax',
  secure: process.env.NODE_ENV === 'production',
  maxAge: IMPERSONATION_MAX_AGE_SECONDS,
} as const;

/**
 * Start acting as another user. Admin-only, and the API validates the target
 * again (active, not an admin, not yourself) before writing the audit row — the
 * cookie is only set once that call succeeds, so the web can never believe in an
 * impersonation the API would refuse.
 */
export async function startImpersonation(userId: string): Promise<ActionState> {
  const t = await getTranslations('impersonation');
  // The REAL session: an admin already impersonating someone must still be able
  // to switch targets, and getSession() would report them as that target.
  const real = await getRealSession();
  if (!real || real.role !== 'admin') return { error: t('errors.notAdmin') };

  let role: Role;
  try {
    const res = await api.post<{ user: { role: string } }>('/admin/impersonation', { userId });
    role = isRole(res.user.role) ? res.user.role : 'patient';
  } catch (e) {
    if (e instanceof ApiError && e.status === 403) return { error: t('errors.forbidden') };
    if (e instanceof ApiError && e.status === 404) return { error: t('errors.notFound') };
    return { error: t('errors.failed') };
  }

  (await cookies()).set(IMPERSONATION_COOKIE, userId, impersonationCookie);
  revalidatePath('/', 'layout');
  redirect(roleHome(role)); // throws NEXT_REDIRECT — must stay outside the try
}

/** Reset back to the admin's own account. Never fails loudly: clearing the
 *  cookie is what actually ends impersonation, so a dead API must not strand
 *  the admin inside someone else's account. */
export async function stopImpersonation(): Promise<void> {
  try {
    await api.delete('/admin/impersonation');
  } catch {
    /* audit row stays open; the cookie below is the real off switch */
  }
  (await cookies()).delete(IMPERSONATION_COOKIE);
  revalidatePath('/', 'layout');
  redirect('/app/admin/users');
}

/* ---------------- Profiles ---------------- */

export async function updateMyProfile(
  _prev: ActionState,
  formData: FormData,
): Promise<ActionState> {
  try {
    await api.patch('/me/profile', {
      fullName: String(formData.get('full_name') ?? '').trim(),
      phone: String(formData.get('phone') ?? '').trim() || undefined,
      city: String(formData.get('city') ?? '').trim() || undefined,
      timezone: String(formData.get('timezone') ?? '').trim() || undefined,
    });
  } catch (e) {
    return { error: msg(e) };
  }
  revalidatePath('/app', 'layout');
  return { error: undefined };
}

/** LGPD: the user's own WhatsApp opt-out, for patients and professionals alike (both
    receive notifications). Patches ONLY notifyWhatsapp — every other profile field is
    left untouched by the API's partial update, so this never races the profile form.
    An unchecked checkbox is absent from FormData, so presence IS the value. */
export async function updateNotifyPreference(
  _prev: ActionState,
  formData: FormData,
): Promise<ActionState> {
  try {
    await api.patch('/me/profile', {
      notifyWhatsapp: formData.get('notify_whatsapp') === 'on',
      // Present only on the professional's form (the questions-alert switch); a patient's
      // form never posts it, and omitting it leaves the column untouched (PATCH contract).
      ...(formData.has('has_question_pref')
        ? { notifyProfileQuestions: formData.get('notify_questions') === 'on' }
        : {}),
      // Inverted on purpose: the box reads "medir meu uso" (opt IN), because a
      // checkbox you have to UNCHECK to be left alone is a dark pattern. The column
      // stores the objection, so the two are mirror images.
      ...(formData.has('has_analytics_pref')
        ? { analyticsOptOut: formData.get('analytics_opt_in') !== 'on' }
        : {}),
      // The rescue field, rendered only when the profile has no phone at all — without one the
      // toggle above is decorative, since notify() skips the send before reaching Meta. Blank
      // is omitted, not sent: the checkbox auto-submits this same form, so an empty box during
      // a toggle must leave the column alone rather than clear it (PATCH contract).
      ...(String(formData.get('phone') ?? '').trim()
        ? { phone: String(formData.get('phone')).trim() }
        : {}),
    });
  } catch (e) {
    return { error: msg(e) };
  }
  revalidatePath('/app', 'layout');
  return { error: undefined };
}

/** Why POST /me/password refused, mirroring `apps/api/src/me/me-errors.ts`. Anything outside
    this set falls back to the generic message rather than a half-translated sentence. */
const PASSWORD_ERROR_CODES = new Set([
  'current_password_invalid',
  'same_as_current_password',
  'password_weak',
  'rate_limited',
  'impersonation_forbidden',
]);

/** Change the signed-in user's own password.
 *
 *  The API does the real work — it re-authenticates with the current password, refuses an
 *  impersonating admin or an acting secretary, and rate-limits wrong guesses. This action only
 *  re-checks the rules the browser already showed (a disabled button is not a validation) and
 *  turns the coded refusal into a sentence.
 *
 *  Returns the `code` as well as the message so the form knows to clear and refocus the
 *  current-password field, keeping the new/confirm pair the user just typed. */
export async function changeMyPassword(
  _prev: ActionState,
  formData: FormData,
): Promise<ActionState> {
  const t = await getTranslations('settings');
  const current = String(formData.get('current_password') ?? '');
  const next = String(formData.get('new_password') ?? '');
  const confirm = String(formData.get('confirm_password') ?? '');

  if (next !== confirm) return { error: t('errMismatch') };
  if (!isPasswordValid({ next, confirm, current })) return { error: t('errWeak') };

  try {
    await api.post('/me/password', { currentPassword: current, newPassword: next });
  } catch (e) {
    const code = codeFrom(e, PASSWORD_ERROR_CODES);
    if (!code) return { error: msg(e) };
    return { error: t(`err.${code}`), code };
  }
  return { error: undefined };
}

/** Saves ONE section of the professional profile at a time (the editor sends a
    hidden `section` field). Only that section's fields go in the patch, so the
    API leaves every other column untouched. */
export async function updateProfessionalProfile(
  _prev: ActionState,
  formData: FormData,
): Promise<ActionState> {
  const section = String(formData.get('section') ?? '');
  const str = (k: string) => String(formData.get(k) ?? '').trim();
  // Each structured address is submitted as one hidden JSON input.
  const patch: Record<string, unknown> = {};
  switch (section) {
    case 'basic': {
      const price = Math.round(Number(formData.get('price') ?? 0) * 100);
      patch.fullName = str('full_name');
      // Always sent, blank included: '' is the explicit "clear my phone" signal the API
      // understands (`dto.phone.trim() || null`). Omitting it on blank would mean "leave
      // untouched" and a pro could never remove a number they no longer use.
      patch.phone = str('phone');
      if (str('council')) patch.council = str('council');
      // Only when the field is actually on the form: a stored registration renders as
      // read-only text (it can no longer be edited here), and '' would reach the API as an
      // invalid registration instead of "left untouched".
      if (str('crm')) patch.crm = str('crm');
      patch.bio = String(formData.get('bio') ?? '');
      patch.location = str('location');
      // The short public link. Only when it carries a value: a blank field means "keep the one
      // you have" — a printed /pro/<slug> must not die because the input was cleared.
      if (str('slug')) patch.slug = str('slug');
      // Only send a non-empty zone — '' would fail the API's @IsIn(IANA) guard.
      if (str('timezone')) patch.timezone = str('timezone');
      // Price and experience left the profile (owner, 2026-11-03): sent only by a form that has them.
      if (formData.has('price')) patch.priceCents = Number.isFinite(price) ? price : 0;
      if (formData.has('experience')) patch.experienceYears = Number(formData.get('experience') ?? 0) || 0;
      patch.telehealth = formData.get('telehealth') === 'on';
      patch.inPerson = formData.get('in_person') === 'on';
      // Only present when the registry fieldset is switching the council TO NONE (dedicated
      // `noneProfessionNames` inputs — not the specialties section's `newSpecialtyNames`).
      // Those names replace declared specialties in the same save. Absent otherwise: a
      // basics save on an already-NONE profile must not rewrite specialties (that list
      // lives in the specialties section). The API also clears stale links when the
      // switch arrives with no profession payload.
      {
        const professions = formData
          .getAll('noneProfessionNames')
          .map(String)
          .filter((v) => v.trim());
        if (professions.length) {
          patch.specialtyIds = [];
          patch.newSpecialtyNames = professions;
          // Headline tracks the profession declared with the switch (otherwise the old
          // regulated specialty line would remain under NONE).
          if (!patch.specialty) patch.specialty = professions[0];
        }
      }
      break;
    }
    default:
      return { error: undefined };
  }
  try {
    await api.patch('/me/pro/profile', patch);
  } catch (e) {
    return { error: await registrationErrorMsg(e) };
  }
  revalidatePath('/app/professional', 'layout');
  return { error: undefined };
}

export async function setMyAvatar(url: string): Promise<ActionState> {
  try {
    await api.patch('/me/avatar', { url });
  } catch (e) {
    return { error: msg(e) };
  }
  revalidatePath('/app', 'layout');
  revalidatePath('/specialists');
  revalidatePath('/', 'layout');
  return { error: undefined };
}

/** Server bookmark for the reusable professional-screen tours. Local storage remains the fast
 * browser backstop, while this prevents a skipped tour from returning after storage eviction or
 * on another device. */
export async function loadScreenTourVersion(id: string): Promise<number> {
  return (await getAgendaPrefs()).screenTours?.[id] ?? 0;
}

export async function saveScreenTourSeen(id: string, version: number): Promise<ActionState> {
  try {
    await api.patch('/me/pro/agenda-prefs', { screenTourId: id, screenTourVersion: version });
  } catch (e) {
    return { error: msg(e) };
  }
  return { error: undefined };
}

/* ---------------- Admin ---------------- */

export async function adminSetUserStatus(
  userId: string,
  status: 'active' | 'pending' | 'suspended',
): Promise<void> {
  try {
    await api.patch(`/admin/profiles/${userId}/status`, { status });
  } catch {
    /* ignore */
  }
  revalidatePath('/app/admin', 'layout');
}

export async function createPlatformStaffMember(input: {
  fullName: string;
  email: string;
  roleId: string;
}): Promise<{ error?: string; tempPassword?: string }> {
  try {
    const result = await api.post<{ tempPassword: string }>('/admin/staff', input);
    revalidatePath('/app/admin/users');
    return { tempPassword: result.tempPassword };
  } catch (error) {
    return { error: msg(error) };
  }
}

export async function assignPlatformStaffRole(profileId: string, roleId: string) {
  try {
    await api.patch(`/admin/staff/${profileId}/role`, { roleId });
    revalidatePath('/app/admin/users');
    return {};
  } catch (error) {
    return { error: msg(error) };
  }
}

export async function savePlatformStaffRole(input: {
  id?: string;
  name: string;
  permissions: string[];
}) {
  try {
    if (input.id) await api.patch(`/admin/staff-roles/${input.id}`, input);
    else await api.post('/admin/staff-roles', input);
    revalidatePath('/app/admin/users');
    return {};
  } catch (error) {
    return { error: msg(error) };
  }
}

export async function deletePlatformStaffRole(id: string) {
  try {
    await api.delete(`/admin/staff-roles/${id}`);
    revalidatePath('/app/admin/users');
    return {};
  } catch (error) {
    return { error: msg(error) };
  }
}

/** The notification bell feed for the signed-in user (empty on failure). */
export async function listNotifications(): Promise<NotificationFeed> {
  try {
    return await api.get<NotificationFeed>('/me/notifications');
  } catch {
    return { unread: 0, items: [] };
  }
}

export async function markNotificationRead(id: string): Promise<void> {
  try {
    await api.patch(`/me/notifications/${id}/read`, {});
  } catch {
    /* best-effort: a failed mark-read just leaves the badge as-is */
  }
}

export async function markAllNotificationsRead(): Promise<void> {
  try {
    await api.patch('/me/notifications/read-all', {});
  } catch {
    /* best-effort */
  }
}

export async function deleteNotification(id: string): Promise<void> {
  try {
    await api.delete(`/me/notifications/${id}`);
  } catch {
    /* best-effort: a failed delete just leaves the item until the next refresh */
  }
}

/** Clear the whole notification feed ("limpar notificações"). */
export async function clearNotifications(): Promise<void> {
  try {
    await api.delete('/me/notifications');
  } catch {
    /* best-effort */
  }
}

/** Admin: release/revoke a professional's premium tools (users page by id,
 *  demo kanban drawer by lead e-mail). */
export async function adminSetPremium(
  target: { userId: string } | { email: string },
  active: boolean,
): Promise<ActionState> {
  const t = await getTranslations('adminInquiries');
  try {
    if ('userId' in target)
      await api.patch(`/admin/professionals/${target.userId}/premium`, { active });
    else await api.patch('/admin/professionals/premium-by-email', { email: target.email, active });
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return { error: t('premiumNoAccount') };
    return { error: msg(e) };
  }
  return { error: undefined };
}

/** Admin: toggle onboarding-paywall grandfathering (subscription_exempt). Keyed by
 *  user id — premium unlocks tools; exemption is what skips the chooser. */
export async function adminSetSubscriptionExempt(
  userId: string,
  exempt: boolean,
): Promise<ActionState> {
  try {
    await api.patch(`/admin/plan/professionals/${userId}/exemption`, { exempt });
  } catch (e) {
    return { error: msg(e) };
  }
  revalidatePath('/app/admin/users');
  return { error: undefined };
}

/* ---------------- In-product support ---------------- */

/** A file already uploaded to the author's own Storage folder. */
type SupportAttachmentUpload = {
  path: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
};

export async function createSupportRequest(input: {
  kind: 'problem' | 'suggestion';
  title: string;
  description: string;
  sourcePath: string;
  attachments?: SupportAttachmentUpload[];
}): Promise<ActionState> {
  try {
    await api.post('/support', input);
  } catch {
    return { error: 'support_request_failed' };
  }
  // The board and the requester's own follow-up list are both stale now.
  revalidatePath('/app/admin/support');
  revalidatePath('/app/patient/settings');
  revalidatePath('/app/professional/settings');
  revalidatePath('/app/admin/settings');
  return { error: undefined };
}

/** The requester's own view of one card (description, public thread, their files). */
export async function getMySupportRequest(
  id: string,
): Promise<{ request?: MySupportRequestDetail; error?: string }> {
  try {
    const request = await api.get<MySupportRequestDetail>(`/support/${id}`);
    return { request };
  } catch {
    return { error: 'support_request_load_failed' };
  }
}

export async function addMySupportComment(
  id: string,
  body: string,
  attachments: SupportAttachmentUpload[] = [],
): Promise<ActionState> {
  try {
    await api.post(`/support/${id}/comments`, { body, attachments });
  } catch {
    return { error: 'support_comment_failed' };
  }
  revalidatePath('/app/admin/support');
  return { error: undefined };
}

/** A short-lived URL for a file the signed-in person is allowed to read.
 *  Storage RLS is the authority: owner folder, admin, or a file the team
 *  attached to a public message on this person's own card. */
export async function getSupportAttachmentUrl(
  path: string,
): Promise<{ url?: string; error?: string }> {
  const supabase = await createClient();
  const { data, error } = await supabase.storage.from('support-images').createSignedUrl(path, 600);
  if (error || !data?.signedUrl) return { error: error?.message ?? 'Arquivo não encontrado.' };
  return { url: data.signedUrl };
}

export async function setSupportRequestStatus(
  id: string,
  status: SupportRequestStatus,
): Promise<ActionState> {
  try {
    await api.patch(`/admin/support/${id}/status`, { status });
  } catch {
    return { error: 'support_status_failed' };
  }
  revalidatePath('/app/admin/support');
  return { error: undefined };
}

export async function setSupportRequestPriority(
  id: string,
  priority: SupportRequestPriority,
): Promise<ActionState> {
  try {
    await api.patch(`/admin/support/${id}/priority`, { priority });
  } catch {
    return { error: 'support_priority_failed' };
  }
  revalidatePath('/app/admin/support');
  return { error: undefined };
}

export async function assignSupportRequest(
  id: string,
  assigneeId: string | null,
): Promise<ActionState> {
  try {
    await api.patch(`/admin/support/${id}/assignee`, { assigneeId });
  } catch {
    return { error: 'support_assignee_failed' };
  }
  revalidatePath('/app/admin/support');
  return { error: undefined };
}

export async function addSupportRequestComment(
  id: string,
  body: string,
  visibility: 'public' | 'internal',
  attachments: SupportAttachmentUpload[] = [],
): Promise<ActionState> {
  try {
    await api.post(`/admin/support/${id}/comments`, { body, visibility, attachments });
  } catch {
    return { error: 'support_comment_failed' };
  }
  revalidatePath('/app/admin/support');
  return { error: undefined };
}

export async function setSupportRequestLabels(
  id: string,
  labelIds: string[],
): Promise<ActionState> {
  try {
    await api.put(`/admin/support/${id}/labels`, { labelIds });
  } catch {
    return { error: 'support_labels_failed' };
  }
  revalidatePath('/app/admin/support');
  return { error: undefined };
}

export async function createSupportLabel(
  name: string,
  color: SupportLabelColor,
): Promise<ActionState> {
  try {
    await api.post('/admin/support/labels', { name, color });
  } catch {
    return { error: 'support_label_failed' };
  }
  revalidatePath('/app/admin/support');
  return { error: undefined };
}

export async function updateSupportLabel(
  labelId: string,
  patch: { name?: string; color?: SupportLabelColor; archived?: boolean },
): Promise<ActionState> {
  try {
    await api.patch(`/admin/support/labels/${labelId}`, patch);
  } catch {
    return { error: 'support_label_failed' };
  }
  revalidatePath('/app/admin/support');
  return { error: undefined };
}

/** A card the backoffice opens for itself: never announced to Slack as inbound. */
export async function createAdminSupportRequest(input: {
  kind: 'problem' | 'suggestion';
  title: string;
  description: string;
  status?: SupportRequestStatus;
  priority?: SupportRequestPriority;
  assigneeId?: string | null;
  labelIds?: string[];
}): Promise<ActionState> {
  try {
    await api.post('/admin/support', input);
  } catch {
    return { error: 'support_request_failed' };
  }
  revalidatePath('/app/admin/support');
  return { error: undefined };
}

/** Returns the plaintext key ONCE — it is never readable again. */
export async function createSupportApiKey(
  name: string,
  scopes: string[],
): Promise<{ key?: string; error?: string }> {
  try {
    const created = await api.post<{ key: string }>('/admin/support/api-keys', { name, scopes });
    revalidatePath('/app/admin/settings');
    return { key: created.key };
  } catch {
    return { error: 'support_api_key_failed' };
  }
}

export async function revokeSupportApiKey(keyId: string): Promise<ActionState> {
  try {
    await api.delete(`/admin/support/api-keys/${keyId}`);
  } catch {
    return { error: 'support_api_key_failed' };
  }
  revalidatePath('/app/admin/settings');
  return { error: undefined };
}

export async function addSupportRequestAttachment(
  id: string,
  attachment: { path: string; fileName: string; mimeType: string; sizeBytes: number },
): Promise<ActionState> {
  try {
    await api.post(`/admin/support/${id}/attachments`, attachment);
  } catch {
    return { error: 'support_attachment_failed' };
  }
  revalidatePath('/app/admin/support');
  return { error: undefined };
}

export async function removeSupportRequestAttachment(
  id: string,
  attachmentId: string,
): Promise<ActionState> {
  try {
    await api.delete(`/admin/support/${id}/attachments/${attachmentId}`);
  } catch {
    return { error: 'support_attachment_remove_failed' };
  }
  revalidatePath('/app/admin/support');
  return { error: undefined };
}

export async function addSupportRequestRelation(
  id: string,
  relatedId: string,
): Promise<ActionState> {
  try {
    await api.post(`/admin/support/${id}/relations`, { relatedId });
  } catch {
    return { error: 'support_relation_failed' };
  }
  revalidatePath('/app/admin/support');
  return { error: undefined };
}

export async function removeSupportRequestRelation(
  id: string,
  relatedId: string,
): Promise<ActionState> {
  try {
    await api.delete(`/admin/support/${id}/relations/${relatedId}`);
  } catch {
    return { error: 'support_relation_remove_failed' };
  }
  revalidatePath('/app/admin/support');
  return { error: undefined };
}

export async function fetchAdminPendingSupportRequests(): Promise<number> {
  return adminCountPendingSupportRequests();
}

/** Admin: short-lived signed URL to view a professional's private verification
 *  document. Storage RLS (owner-or-admin read) is the real gate; the role check
 *  here just fails fast with a clear error. */
export async function adminGetVerificationDocUrl(
  path: string,
): Promise<{ url?: string; error?: string }> {
  const session = await getSession();
  if (session?.role !== 'admin') return { error: 'Sem permissão.' };
  const supabase = await createClient();
  const { data, error } = await supabase.storage
    .from('verification-docs')
    .createSignedUrl(path, 600);
  if (error || !data?.signedUrl) return { error: error?.message ?? 'Documento não encontrado.' };
  return { url: data.signedUrl };
}

